import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Card,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    Modal,
    ModalContent,
    ModalTitle,
    Select,
    Typography
} from "@deere/fuel-react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {fetchSuppliersList} from "../shared/services/supplierSearchService";
import {useAuthConfig} from "../shared/helper";
import {Controller, useForm} from "react-hook-form";
import {Loading} from "../shared/Loading";
import {GroupCard} from "./GroupCard";

export const Groups = () => {
    let authAxios = useAuthConfig();

    const [supplierOptions, setSupplierOptions] = useState([]);
    const [fyOptions, setFyOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState({});

    const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
    const [modalSupplierOptions, setModalSupplierOptions] = useState([]);

    const searchForm = useForm({
        defaultValues: {
            supplierNum: "",
            parentName: "",
            fiscalYear: "",
        }
    });

    const createGroupFormDefaults = {
        groupName: '',
        suppliers: [],
        fiscalYear: '',
    };

    const createGroupForm = useForm({
        defaultValues: createGroupFormDefaults,
    });

    const updateGroups = (oldGroup, newGroup) => {
        const oldParentName = Object.keys(oldGroup)[0];
        if (!newGroup || Object.keys(newGroup).length === 0) {
            const updatedGroups = {...groups};
            delete updatedGroups[oldParentName];
            setGroups(updatedGroups);
            return;
        }

        const newParentName = Object.keys(newGroup)[0];
        if (oldParentName !== newParentName) {
            const updatedGroups = {...groups, ...newGroup};
            delete updatedGroups[oldParentName];
            setGroups(updatedGroups);
        } else {
            setGroups({...groups, ...newGroup});
        }
    }

    const getFyList = async () => {
        setLoading(true)
        try {
            const res = await authAxios.get(`/calendar/fiscal-years`);

            // set Fiscal years sorted descending
            const fiscalYears = res.data.sort((a, b) => b - a);

            //Add in the next fiscal year
            setFyOptions([fiscalYears[0] + 1, ...fiscalYears]);

            // reset forms so that their default values are changed
            searchForm.reset({
                supplierNum: searchForm.getValues("supplierNum"),
                parentName: searchForm.getValues("parentName"),
                fiscalYear: fiscalYears[0],
            });

            createGroupForm.reset({
                ...createGroupFormDefaults,
                fiscalYear: fiscalYears[0],
            });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const getSuppliersList = async (value) => {
        try {
            const res = await fetchSuppliersList(authAxios, value);
            setSupplierOptions(res);
            setLoading(false);
        } catch (e) {
            console.error(e);
        }
    };

    const zeroOrOneField = (supplierNumber, parentName) => (!supplierNumber && !parentName) || (supplierNumber && !parentName) || (parentName && !supplierNumber);

    /**
     *
     * @param {{
     *   fiscalYear: string[] | string;
     *   supplierNum: string[] | string;
     * }|{
     *   parentName: string[] | string;
     *   supplierNum: string[] | string;
     * }} data
     * @returns
     */
    function fetchSupplierGroups(data) {
        return authAxios.post("/supplierGroups", {
            ...data,
        });
    }

    const submitSupplierGroupSearch = async (data) => {
        setLoading(true);
        try {
            const res = await fetchSupplierGroups({
                ...data,
                supplierNum: data?.supplierNum?.supplierNumber || "",
            });

            setGroups(res.data);
        } catch (e) {

        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getFyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createGroupModalHandlers = {
        onOpen() {
            setCreateGroupModalOpen(true);
        },

        onClose() {
            setCreateGroupModalOpen(false);
            createGroupForm.reset();
        },

        onSubmit(data) {
            authAxios.post("/parentGroup", {
                oldParentName: '',
                newParentName: data.groupName,
                suppliers: data.suppliers,
                fiscalYear: data.fiscalYear
            }).then(res => {
                setGroups(prevGroups => ({...prevGroups, ...{[data.groupName]: res.data[data.groupName]}}));
                createGroupModalHandlers.onClose();
            }).catch(err => {
                console.error("Error creating Group", err);
            })
        },
    };

    return (
        <>
            <Card xs={12} md={8} sx={{padding: 4}}>
                <form onSubmit={searchForm.handleSubmit(submitSupplierGroupSearch)}>
                    <Grid container>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth error={!!searchForm.formState.errors.supplierNum}>
                                <InputLabel htmlFor="supplierNum">
                                    Select Supplier{searchForm.formState.errors.supplierNum ?
                                    <span style={{color: 'var(--fuel-color-error, #d32f2f)'}}> **</span> : ''}
                                </InputLabel>
                                <Controller
                                    name="supplierNum"
                                    control={searchForm.control}
                                    rules={{
                                        validate: value => zeroOrOneField(value, searchForm.getValues("parentName")) || "Please fill out only one field: either Supplier or Group Name, not both."
                                    }}
                                    render={({onChange}) => (
                                        <Autocomplete
                                            size="small"
                                            id="supplierNum"
                                            options={supplierOptions}
                                            getOptionLabel={(option) => `${option.supplierName} - ${parseInt(option.supplierNumber)}`}
                                            getOptionSelected={(option, value) => option.supplierNumber === value.supplierNumber}
                                            onChange={(e, value) => {
                                                searchForm.clearErrors();
                                                onChange(value)
                                            }}
                                            onInputChange={(e, value) => {
                                                if (value.length > 2)
                                                    getSuppliersList(value)
                                            }}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    placeholder="Search Supplier name or number (enter 3 or more characters)"
                                                    variant="outlined"
                                                    error={!!searchForm.formState.errors.supplierNum}
                                                    helperText={searchForm.formState.errors?.supplierNum?.message}
                                                />}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth error={!!searchForm.formState.errors.parentName}>
                                <InputLabel htmlFor="parentName">
                                    Group Name{searchForm.formState.errors.parentName ?
                                    <span style={{color: 'var(--fuel-color-error, #d32f2f)'}}> **</span> : ''}
                                </InputLabel>
                                <Controller
                                    control={searchForm.control}
                                    name="parentName"
                                    rules={{
                                        validate: value => zeroOrOneField(searchForm.getValues("supplierNum"), value) || "Please fill out only one field: either Supplier or Group Name, not both."
                                    }}
                                    render={(field, fieldState) => (
                                        <TextField
                                            {...field}
                                            id="parentName"
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter group name"
                                            style={{width: "80%"}}
                                            onChange={e => {
                                                searchForm.clearErrors();
                                                return field.onChange(e.target.value)
                                            }}
                                            error={fieldState.invalid}
                                            helperText={searchForm.formState.errors[field.name]?.message}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl required fullWidth error={!!searchForm.formState.errors.fiscalYear}>
                                <InputLabel id={"fiscalYear"}>Fiscal Year</InputLabel>
                                <Controller
                                    rules={{required: true}}
                                    control={searchForm.control}
                                    name={"fiscalYear"}
                                    render={({onChange, value}) => (
                                        <Select native
                                                labelId={"fiscalYear"}
                                                id={"fiscalYear"}
                                                value={value || ""}
                                                onChange={(e) => {
                                                    onChange(e);
                                                }}>
                                            {fyOptions?.length ? fyOptions.map((item) => {
                                                return (
                                                    <option
                                                        key={item}
                                                        value={item}>{item}</option>
                                                );
                                            }) : <></>}
                                        </Select>
                                    )}
                                />
                                {!!searchForm.formState.errors.fiscalYear &&
                                    <FormHelperText>Fiscal Year is a required field</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}
                              style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <Button variant="primary" size="large" type="submit">
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Card>

            <Box sx={{display: 'flex', justifyContent: 'center', padding: 2}}>
                <Button onClick={createGroupModalHandlers.onOpen} variant="secondary">
                    <Typography variant="body2" color="primary">
                        Create Group
                    </Typography>
                </Button>
            </Box>

            <Box sx={{py: 4}}>
                {loading ? <Loading/> :
                    groups && Object.keys(groups).length > 0 ?
                        Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([parentName, suppliers]) => (
                            <GroupCard key={parentName} parentName={parentName} suppliers={suppliers}
                                       updateGroups={updateGroups} fyOptions={fyOptions}/>
                        ))
                        : <></>
                }
            </Box>

            <Modal
                open={createGroupModalOpen}
                onClose={createGroupModalHandlers.onClose}
                aria-labelledby="create-group-modal-title"
                aria-describedby="create-group-modal-description"
            >
                <ModalContent sx={{maxWidth: 800, width: '100%'}}>
                    <ModalTitle
                        component="h5"
                        id="create-group-modal-title"
                        sx={{padding: 0, paddingBottom: 2}}
                        variant="h5"
                    >
                        Create Group
                    </ModalTitle>
                    <Box>
                        <form onSubmit={createGroupForm.handleSubmit(createGroupModalHandlers.onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name="groupName"
                                            control={createGroupForm.control}
                                            defaultValue=""
                                            render={(field, fieldState) => (
                                                <TextField
                                                    {...field}
                                                    error={fieldState.invalid}
                                                    helperText={createGroupForm.formState.errors[field.name]?.message}
                                                    label="Group Name"
                                                    variant="outlined"
                                                />
                                            )}
                                            rules={{
                                                required: "Group Name is required",
                                                minLength: {
                                                    value: 2,
                                                    message: "Group Name must be at least 2 characters long"
                                                },

                                                async validate(value) {
                                                    try {
                                                        const res = (await fetchSupplierGroups({
                                                            parentName: [value],
                                                            fiscalYear: createGroupForm.getValues("fiscalYear")
                                                        })).data;

                                                        return Object.keys(res).length === 0 || "A group with this name already exists";
                                                    } catch (e) {
                                                        console.error(e);
                                                        return 'Error checking if group with this name already exists: ' + (e.message ?? 'unknown error');
                                                    }
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <Controller
                                            name="suppliers"
                                            control={createGroupForm.control}
                                            render={(field, fieldState) => (
                                                <Autocomplete
                                                    {...field}
                                                    multiple
                                                    options={modalSupplierOptions}
                                                    getOptionLabel={(option) => `${option.supplierName} - ${parseInt(option.supplierNumber)}`}
                                                    getOptionSelected={(option, value) => option.supplierNumber === value.supplierNumber}
                                                    onChange={(e, value) => void field.onChange(value)}
                                                    onInputCapture={async (e) => {
                                                        const {value} = e.target;
                                                        if (value.length <= 2)
                                                            return;

                                                        try {
                                                            const res = await fetchSuppliersList(authAxios, value);
                                                            setModalSupplierOptions(res);
                                                        } catch (e) {
                                                            console.error(e);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Suppliers"
                                                            variant="outlined"
                                                            error={fieldState.invalid}
                                                            helperText={createGroupForm.formState.errors[field.name]?.message}
                                                        />
                                                    )}
                                                />
                                            )}
                                            rules={{
                                                async validate(value) {
                                                    if (value.length < 2) {
                                                        return "At least two suppliers are required";
                                                    }
                                                    return await fetchSupplierGroups({
                                                        supplierNum: [...value.map(s => s.supplierNumber)],
                                                        fiscalYear: createGroupForm.getValues("fiscalYear"),
                                                        strictSearch: true
                                                    }).then((response) => {
                                                        if (!!response.data && Object.keys(response.data).length > 0) {
                                                            console.error("Response data:", response.data);
                                                            return "A Supplier is already in a group";
                                                        }
                                                        return true;
                                                    }).catch(() => {
                                                        return "Error checking if supplier exists in group";
                                                    })
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id={"fiscalYear"}>Fiscal Year</InputLabel>
                                        <Controller
                                            name="fiscalYear"
                                            control={createGroupForm.control}
                                            render={(field, fieldState) => <>
                                                <Select
                                                    {...field}
                                                    error={fieldState.invalid}
                                                    label="Fiscal Year"
                                                    native
                                                >
                                                    {/* Current fiscal year and next one */}
                                                    {fyOptions && [fyOptions[0], fyOptions[1]].map((item) => (
                                                        <option key={item} value={item}>{item}</option>
                                                    ))}
                                                </Select>
                                                <FormHelperText error={fieldState.invalid}>
                                                    {createGroupForm.formState.errors[field.name]?.message}
                                                </FormHelperText>
                                            </>}
                                            rules={{required: "Fiscal Year is required"}}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} style={{marginTop: '16px'}} justifyContent="flex-end">
                                    <Box display="flex" justifyContent="flex-end">
                                        {createGroupForm?.formState?.isLoading ? <Loading/> :
                                            <Button
                                                variant={"primary"}
                                                type={"submit"}
                                                // onClick={createGroupForm.handleSubmit(createGroupModalHandlers.onSubmit)}
                                                disabled={Object.keys(createGroupForm.formState.errors).length > 0}
                                            >
                                                Create Group
                                            </Button>}
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                </ModalContent>
            </Modal>
        </>
    );
}
